import { actions, idleState, loadingState, failedState } from "@grandvisionhq/state";
import { isDefined } from "@grandvisionhq/utils";
import { startConfigurator, nextStep, nextStepSuccess, previousStepSuccess, resetStepVisited, toggleFooter, closeFooter, openFooter, finish, finishFailed, finishSuccess } from "./actions.js";
const createInitialConfiguratorState = (stateOverrides) => ({
  currentStep: 0,
  stepSequence: [],
  stepsAttempted: /* @__PURE__ */ new Set(),
  stepsVisited: [stateOverrides?.stepSequence?.[0] ?? ""],
  calls: {
    finish: idleState()
  },
  isFooterOpen: false,
  ...stateOverrides
});
const setStateById = (state, id, cb) => {
  const configuratorState = state[id];
  return configuratorState ? {
    ...state,
    [id]: {
      ...configuratorState,
      ...cb(configuratorState)
    }
  } : state;
};
const initialState = {};
const reducer = actions.reducer(
  initialState,
  /**
   * Set the correct productSku and always reset the configurator to the initial state
   */
  actions.on(startConfigurator, (s, { payload: { id, stepSequence } }) => ({
    ...s,
    [id]: {
      /**
       * Reset entire state for this configurator.
       * setStateById merges the configurator ID state slice, we don't want that here.
       */
      ...createInitialConfiguratorState({ stepSequence })
    }
  })),
  /**
   * When user tries to move on to the next step record so we mark the form as submitted
   *
   * This will reveal validation errors
   */
  actions.on(
    nextStep,
    (s, { payload: { id } }) => setStateById(s, id, (c) => ({
      ...c,
      stepsAttempted: /* @__PURE__ */ new Set([...c.stepsAttempted ?? [], c.currentStep])
    }))
  ),
  /**
   * When the steps change is allowed it's communicated by dispatching the nextStepSuccess action
   */
  actions.on(
    nextStepSuccess,
    (s, { payload: { id, newStep } }) => setStateById(s, id, (c) => {
      const newStepsVisited = new Set(
        [...c.stepsVisited, c.stepSequence[newStep]].filter(isDefined)
      );
      return {
        ...c,
        currentStep: newStep,
        stepsVisited: [...newStepsVisited]
      };
    })
  ),
  /**
   * When the current step is moved back it's communicated by dispatching the previousStepSuccess action
   *
   * When user returns from step 3 to step 2, both 3 (currentStep) and 2 (newStep) are removed so we don't show error messages when returning
   */
  actions.on(
    previousStepSuccess,
    (s, { payload: { id, currentStepNumber, newStep } }) => setStateById(s, id, (c) => ({
      ...c,
      currentStep: newStep,
      stepsAttempted: new Set(
        [...c.stepsAttempted].filter((step) => step !== currentStepNumber && step !== newStep)
      )
    }))
  ),
  actions.on(
    resetStepVisited,
    (s, { payload: { id, step } }) => setStateById(s, id, (c) => {
      const newStepsVisited = c.stepsVisited.filter((visitedStep) => visitedStep !== step);
      return {
        ...c,
        stepsVisited: newStepsVisited
      };
    })
  ),
  /**
   * Toggles footer
   */
  actions.on(
    toggleFooter,
    (s, { payload: { id } }) => setStateById(s, id, (c) => ({
      ...c,
      isFooterOpen: !c.isFooterOpen
    }))
  ),
  actions.on(
    closeFooter,
    (s, { payload: { id } }) => setStateById(s, id, (c) => ({
      ...c,
      isFooterOpen: false
    }))
  ),
  actions.on(
    openFooter,
    (s, { payload: { id } }) => setStateById(s, id, (c) => ({
      ...c,
      isFooterOpen: true
    }))
  ),
  /**
   * Tracks the status of the finish call
   */
  actions.on(
    finish,
    (s, { payload: { id } }) => setStateById(s, id, (c) => ({
      ...c,
      calls: {
        ...c.calls,
        finish: loadingState()
      }
    }))
  ),
  actions.on(
    finishFailed,
    (s, { payload: { id, reason } }) => setStateById(s, id, (c) => ({
      ...c,
      calls: {
        ...c.calls,
        finish: failedState(reason)
      }
    }))
  ),
  actions.on(
    finishSuccess,
    (s, { payload: { id } }) => setStateById(s, id, (c) => ({
      ...c,
      calls: {
        ...c.calls,
        finish: idleState()
      }
    }))
  )
);
export {
  initialState,
  reducer
};
//# sourceMappingURL=reducer.js.map
